import type { BananImageResult, GeneratedAd, KlingVideoResult } from "../components/adsUtils";
import { mergeCompletedImageAds, mergeGeneratedAds } from "./adsDedup";
import type { AdsWorkspaceState } from "./useAdsWorkspaceStore.types";

// Pure job→ad mapping for the three async generators. Each takes the minimal
// state slice it reads plus the completion payload and returns the partial
// store update (or `null` when the job isn't tracked, i.e. a no-op). The store
// actions are thin `set((s) => applyX(s, …) ?? s)` wrappers so this branchy
// metadata-threading logic stays unit-testable in isolation.

type VideoCompletionResult = { videoUrl?: string; thumbnailUrl?: string; error?: string };

type KlingCompletionState = Pick<AdsWorkspaceState, "pendingKlingJobs" | "generatedAds">;
type BananCompletionState = Pick<AdsWorkspaceState, "pendingBananJobs" | "generatedAds">;
type HiggsfieldCompletionState = Pick<AdsWorkspaceState, "pendingHiggsfieldJobs" | "generatedAds">;

export function applyKlingJobCompletion(
  state: KlingCompletionState,
  jobId: string,
  videoUrl?: string,
  thumbnailUrl?: string,
  videos?: VideoCompletionResult[],
): Partial<AdsWorkspaceState> | null {
  const job = state.pendingKlingJobs.find((j) => j.jobId === jobId);
  if (!job) return null;

  // 30s two-segment: Segment A completed — store URL, don't create ad yet
  if (job.segmentIndex === 0 && job.totalSegments === 2) {
    const segAUrl = videoUrl ?? videos?.[0]?.videoUrl;
    return {
      pendingKlingJobs: state.pendingKlingJobs.map((j) =>
        j.jobId === jobId
          ? { ...j, status: "completed" as const, segmentAVideoUrl: segAUrl, thumbnailUrl }
          : j,
      ),
      updatedAt: Date.now(),
    };
  }

  // 30s two-segment: Segment B completed — store URL on the segment-B
  // pending entry so the stitch step (triggered from the poller) can find
  // it regardless of which segment finishes first. Keep the job in
  // pendingKlingJobs; the poller removes it after stitch persists.
  if (job.segmentIndex === 1 && job.totalSegments === 2) {
    const segBUrl = videoUrl ?? videos?.[0]?.videoUrl;
    return {
      pendingKlingJobs: state.pendingKlingJobs.map((j) =>
        j.jobId === jobId
          ? { ...j, status: "completed" as const, videoUrl: segBUrl, thumbnailUrl }
          : j,
      ),
      updatedAt: Date.now(),
    };
  }

  // 30s two-segment: stitched video ready — create final ad
  if (job.stitchedVideoUrl) {
    const updatedPending = state.pendingKlingJobs.filter(
      (j) => j.jobId !== jobId && j.jobId !== job.pairedSegmentJobId,
    );
    const videoResult: KlingVideoResult = {
      id: jobId,
      status: "done",
      video_output: job.stitchedVideoUrl,
      video_thumbnail: thumbnailUrl,
    };
    const metadata: Record<string, unknown> = {
      source: "vid",
      generator: "kling",
      assetKind: "video-ad",
      jobId,
      is30s: true,
    };
    if (job.productId) metadata.productId = job.productId;
    if (job.productName) metadata.productName = job.productName;
    if (job.sourceVideoScriptAngle) metadata.sourceVideoScriptAngle = job.sourceVideoScriptAngle;
    if (job.targetProblem) metadata.targetProblem = job.targetProblem;
    if (job.planRunId) metadata.planRunId = job.planRunId;
    if (job.cloneSourceArtifactId) metadata.cloneSourceArtifactId = job.cloneSourceArtifactId;
    if (typeof job.videoIndex === "number") metadata.videoIndex = job.videoIndex;
    const newAd: GeneratedAd = { type: "video", result: videoResult, metadata };
    return {
      pendingKlingJobs: updatedPending,
      generatedAds: mergeGeneratedAds(state.generatedAds, [newAd]),
      updatedAt: Date.now(),
    };
  }

  const updatedPending = state.pendingKlingJobs.filter((j) => j.jobId !== jobId);

  // Batch job: one GeneratedAd per successful video, mapping lineage via batchItems
  if (job.batchItems && videos && videos.length > 0) {
    const newAds: GeneratedAd[] = [];
    videos.forEach((v, i) => {
      if (!v.videoUrl) return;
      const lineage = job.batchItems?.[i];
      const metadata: Record<string, unknown> = {};
      if (job.productId) metadata.productId = job.productId;
      if (job.productName) metadata.productName = job.productName;
      if (lineage?.sourceImageJobId) metadata.sourceImageJobId = lineage.sourceImageJobId;
      if (lineage?.sourceImageUrl) metadata.sourceImageUrl = lineage.sourceImageUrl;
      if (lineage?.sourceResearchAngle) metadata.sourceResearchAngle = lineage.sourceResearchAngle;
      if (lineage?.sourceVideoScriptAngle)
        metadata.sourceVideoScriptAngle = lineage.sourceVideoScriptAngle;
      if (lineage?.targetProblem) metadata.targetProblem = lineage.targetProblem;
      if (lineage?.mode) metadata.klingMode = lineage.mode;
      if (job.planRunId) metadata.planRunId = job.planRunId;
      if (lineage?.cloneSourceArtifactId)
        metadata.cloneSourceArtifactId = lineage.cloneSourceArtifactId;
      if (typeof lineage?.segmentIndex === "number")
        metadata.pairedSegmentIndex = lineage.segmentIndex;
      if (typeof lineage?.totalSegments === "number") metadata.totalSegments = lineage.totalSegments;
      if (typeof lineage?.videoIndex === "number") metadata.videoIndex = lineage.videoIndex;

      const videoResult: KlingVideoResult = {
        id: `${jobId}_${i}`,
        status: "done",
        video_output: v.videoUrl,
        video_thumbnail: v.thumbnailUrl,
      };
      newAds.push({
        type: "video",
        result: videoResult,
        ...(Object.keys(metadata).length > 0 ? { metadata } : {}),
      });
    });

    return {
      pendingKlingJobs: updatedPending,
      generatedAds: mergeGeneratedAds(state.generatedAds, newAds),
      updatedAt: Date.now(),
    };
  }

  // Single-video job: original behavior
  const videoResult: KlingVideoResult = {
    id: jobId,
    status: "done",
    video_output: videoUrl,
    video_thumbnail: thumbnailUrl,
  };
  const isHeyGen = job.provider === "heygen";
  const metadata: Record<string, unknown> = {
    source: isHeyGen ? "heygen" : "vid",
    generator: isHeyGen ? "heygen" : "kling",
    assetKind: "video-ad",
    jobId,
  };
  if (job.provider) metadata.provider = job.provider;
  if (job.heygenSessionId) metadata.heygenSessionId = job.heygenSessionId;
  if (job.agentRuntimeSessionId) metadata.sessionId = job.agentRuntimeSessionId;
  if (job.productId) metadata.productId = job.productId;
  if (job.productName) metadata.productName = job.productName;
  if (job.sourceImageJobId) metadata.sourceImageJobId = job.sourceImageJobId;
  if (job.sourceImageUrl) metadata.sourceImageUrl = job.sourceImageUrl;
  if (job.sourceResearchAngle) metadata.sourceResearchAngle = job.sourceResearchAngle;
  if (job.sourceVideoScriptAngle) metadata.sourceVideoScriptAngle = job.sourceVideoScriptAngle;
  if (job.targetProblem) metadata.targetProblem = job.targetProblem;
  if (job.planRunId) metadata.planRunId = job.planRunId;
  if (job.cloneSourceArtifactId) metadata.cloneSourceArtifactId = job.cloneSourceArtifactId;
  if (typeof job.videoIndex === "number") metadata.videoIndex = job.videoIndex;
  const newAd: GeneratedAd = { type: "video", result: videoResult, metadata };

  return {
    pendingKlingJobs: updatedPending,
    generatedAds: mergeGeneratedAds(state.generatedAds, [newAd]),
    updatedAt: Date.now(),
  };
}

export function applyBananJobCompletion(
  state: BananCompletionState,
  jobId: string,
  imageUrl?: string,
  imageUrls?: string[],
): Partial<AdsWorkspaceState> | null {
  const job = state.pendingBananJobs.find((j) => j.jobId === jobId);
  if (!job) return null;

  const updatedPending = state.pendingBananJobs.filter((j) => j.jobId !== jobId);

  // Multi-image jobs: create one GeneratedAd per URL. Covers two shapes:
  // (1) explicit batch with per-item lineage (batchItems set — Marcus's
  // banan_batch_dispatched, ugc_element_refs_dispatched), and (2) the
  // legacy multi-variant path (`dispatchImageAd` with `imageCount > 1`)
  // where `batchItems` is undefined but the agent still produced N URLs.
  // The previous single-fallback dropped N-1 images and the user had to
  // click a SessionPill to surface them via the chat-history rebuild.
  const multipleUrls = imageUrls && imageUrls.length > 1;
  if ((job.batchItems || multipleUrls) && imageUrls && imageUrls.length > 0) {
    const nowIso = new Date().toISOString();
    const newAds: GeneratedAd[] = imageUrls.map((url, i) => {
      const lineage = job.batchItems?.[i];
      const metadata: Record<string, unknown> = { jobId, batchIndex: i };
      if (job.assetKind) metadata.assetKind = job.assetKind;
      if (job.productId) metadata.productId = job.productId;
      if (job.productName) metadata.productName = job.productName;
      if (job.sessionId) metadata.sessionId = job.sessionId;
      if (job.templateId) metadata.templateId = job.templateId;
      if (job.templateName) metadata.templateName = job.templateName;
      if (lineage?.sourceResearchAngle) metadata.sourceResearchAngle = lineage.sourceResearchAngle;
      if (lineage?.targetProblem) metadata.targetProblem = lineage.targetProblem;
      if (lineage?.overlayText) metadata.overlayText = lineage.overlayText;
      if (lineage?.ugcSceneSlot) metadata.ugcSceneSlot = lineage.ugcSceneSlot;
      if (lineage?.ugcSceneIndex !== undefined) metadata.ugcSceneIndex = lineage.ugcSceneIndex;
      if (lineage?.ugcVideoIndex !== undefined) metadata.ugcVideoIndex = lineage.ugcVideoIndex;
      if (lineage?.sourceVideoScriptAngle)
        metadata.sourceVideoScriptAngle = lineage.sourceVideoScriptAngle;
      // Portrait lineage — ReferenceImagesStep.portraitsForAvatar matches
      // on metadata.sourceAvatarPersonaId, so without these copies the
      // avatar tiles never resolve their portrait even though the image
      // landed in the workspace store.
      if (lineage?.sourceAvatarPersonaId)
        metadata.sourceAvatarPersonaId = lineage.sourceAvatarPersonaId;
      if (lineage?.sourceHookType) metadata.sourceHookType = lineage.sourceHookType;
      if (lineage?.avatarName) metadata.avatarName = lineage.avatarName;
      // Nest element / text-frame lineage so AdsWorkspace.referenceImages
      // picks these up in the Reference Images tab. Its filter matches the
      // same nested shape the backend writes
      // (bananArtifactPersister.ts:181, AdsWorkspace.tsx:657/681). Flat
      // lineage fields alone never surfaced in the UI because the filter
      // only checks meta.ugcElement / meta.ugcTextFrame.
      if (lineage?.elementKey && lineage?.kind) {
        metadata.ugcElement = {
          elementKey: lineage.elementKey,
          kind: lineage.kind,
          ...(job.planRunId ? { planRunId: job.planRunId } : {}),
        };
      } else if (lineage?.assignedBeat) {
        metadata.ugcTextFrame = {
          assignedBeat: lineage.assignedBeat,
          ...(job.planRunId ? { planRunId: job.planRunId } : {}),
        };
      }

      const imageResult: BananImageResult = {
        id: `${jobId}_${i}`,
        status: "done",
        output: url,
        created_at: nowIso,
      };
      return {
        type: "image" as const,
        result: imageResult,
        ...(job.templateName ? { templateName: job.templateName } : {}),
        ...(job.templateId ? { templateId: job.templateId } : {}),
        ...(Object.keys(metadata).length > 0 ? { metadata } : {}),
      };
    });

    return {
      pendingBananJobs: updatedPending,
      generatedAds: mergeCompletedImageAds(state.generatedAds, newAds),
      updatedAt: Date.now(),
    };
  }

  // Single-image job: original behavior
  const imageResult: BananImageResult = {
    id: jobId,
    status: "done",
    output: imageUrl ?? imageUrls?.[0],
    created_at: new Date().toISOString(),
  };

  // Thread lineage metadata from the pending job into the ad's metadata
  // so it persists to the Assets tab and context snapshots.
  const metadata: Record<string, unknown> = { jobId };
  if (job.assetKind) metadata.assetKind = job.assetKind;
  if (job.productId) metadata.productId = job.productId;
  if (job.productName) metadata.productName = job.productName;
  if (job.sessionId) metadata.sessionId = job.sessionId;
  if (job.templateId) metadata.templateId = job.templateId;
  if (job.templateName) metadata.templateName = job.templateName;
  if (job.sourceResearchAngle) metadata.sourceResearchAngle = job.sourceResearchAngle;
  if (job.targetProblem) metadata.targetProblem = job.targetProblem;
  if (job.overlayText) metadata.overlayText = job.overlayText;

  const newAd: GeneratedAd = {
    type: "image",
    result: imageResult,
    ...(job.templateName ? { templateName: job.templateName } : {}),
    ...(job.templateId ? { templateId: job.templateId } : {}),
    ...(Object.keys(metadata).length > 0 ? { metadata } : {}),
  };

  return {
    pendingBananJobs: updatedPending,
    generatedAds: mergeCompletedImageAds(state.generatedAds, [newAd]),
    updatedAt: Date.now(),
  };
}

export function applyHiggsfieldJobCompletion(
  state: HiggsfieldCompletionState,
  jobId: string,
  videoUrl?: string,
  thumbnailUrl?: string,
  videos?: VideoCompletionResult[],
): Partial<AdsWorkspaceState> | null {
  const job = state.pendingHiggsfieldJobs.find((j) => j.jobId === jobId);
  if (!job) return null;
  const updatedPending = state.pendingHiggsfieldJobs.filter((j) => j.jobId !== jobId);

  // Batch job: one GeneratedAd per successful video, mapping lineage via batchItems
  if (job.batchItems && videos && videos.length > 0) {
    const newAds: GeneratedAd[] = [];
    videos.forEach((v, i) => {
      if (!v.videoUrl) return;
      const lineage = job.batchItems?.[i];
      const metadata: Record<string, unknown> = {};
      if (job.productId) metadata.productId = job.productId;
      if (job.productName) metadata.productName = job.productName;
      if (lineage?.videoTypeId) metadata.videoTypeId = lineage.videoTypeId;
      if (lineage?.higgsfieldMode) metadata.higgsfieldMode = lineage.higgsfieldMode;
      if (lineage?.variantAngle) metadata.variantAngle = lineage.variantAngle;
      if (lineage?.aspectRatio) metadata.aspectRatio = lineage.aspectRatio;
      if (lineage?.duration) metadata.duration = lineage.duration;
      if (job.sessionId) metadata.sessionId = job.sessionId;
      if (job.sourceMedias && job.sourceMedias.length > 0) {
        metadata.sourceMedias = job.sourceMedias;
      }
      metadata.assetKind = "video-ad";
      metadata.generator = job.generator ?? "higgsfield";

      const videoResult: KlingVideoResult = {
        id: `${jobId}_${i}`,
        status: "done",
        video_output: v.videoUrl,
        video_thumbnail: v.thumbnailUrl,
      };
      newAds.push({
        type: "video",
        result: videoResult,
        metadata,
      });
    });

    return {
      pendingHiggsfieldJobs: updatedPending,
      generatedAds: mergeGeneratedAds(state.generatedAds, newAds),
      updatedAt: Date.now(),
    };
  }

  // Single-video job
  const videoResult: KlingVideoResult = {
    id: jobId,
    status: "done",
    video_output: videoUrl,
    video_thumbnail: thumbnailUrl,
  };
  const metadata: Record<string, unknown> = {
    assetKind: "video-ad",
    generator: job.generator ?? "higgsfield",
  };
  if (job.productId) metadata.productId = job.productId;
  if (job.productName) metadata.productName = job.productName;
  if (job.videoTypeId) metadata.videoTypeId = job.videoTypeId;
  if (job.higgsfieldMode) metadata.higgsfieldMode = job.higgsfieldMode;
  if (job.sessionId) metadata.sessionId = job.sessionId;
  if (job.sourceMedias && job.sourceMedias.length > 0) {
    metadata.sourceMedias = job.sourceMedias;
  }
  const newAd: GeneratedAd = { type: "video", result: videoResult, metadata };

  return {
    pendingHiggsfieldJobs: updatedPending,
    generatedAds: mergeGeneratedAds(state.generatedAds, [newAd]),
    updatedAt: Date.now(),
  };
}
